import { create } from 'zustand';

import type {
    CreateFormFieldPayload,
    CreateSectionPayload,
    FieldType,
    FormMode,
    Visibility,
} from '@/types/form';

function makeDefaultField(order: number): CreateFormFieldPayload {
    return {
        label: '',
        field_type: 'short_text' as FieldType,
        is_required: false,
        order,
        config: {},
        description: '',
        points: 0,
        correct_answer: {},
        shuffle_choices: false,
    };
}

function makeDefaultSection(order: number): CreateSectionPayload {
    return {
        title: order === 0 ? '' : `Seccion ${order + 1}`,
        description: '',
        order,
        fields: [makeDefaultField(0)],
    };
}

export interface FormSettings {
    title: string;
    description: string;
    visibility: Visibility;
    requires_auth: boolean;
    one_response_per_user: boolean;
    closes_at: string;
    mode: FormMode;
    shuffle_options: boolean;
    allow_edit_response: boolean;
    show_progress_bar: boolean;
    confirmation_message: string;
    theme_color: string;
    theme_image_url: string;
    collect_email: boolean;
}

interface State {
    sections: CreateSectionPayload[];
    selectedSectionIndex: number;
    selectedFieldIndex: number | null;
    formSettings: FormSettings;
    isDirty: boolean;
}

interface Actions {
    addSection: () => void;
    removeSection: (index: number) => void;
    reorderSections: (from: number, to: number) => void;
    updateSection: (index: number, partial: Partial<Pick<CreateSectionPayload, 'title' | 'description'>>) => void;
    addField: (sectionIndex: number) => void;
    removeField: (sectionIndex: number, fieldIndex: number) => void;
    reorderFields: (sectionIndex: number, from: number, to: number) => void;
    updateField: (sectionIndex: number, fieldIndex: number, field: CreateFormFieldPayload) => void;
    updateSettings: (partial: Partial<FormSettings>) => void;
    setSelectedSection: (index: number) => void;
    setSelectedField: (index: number | null) => void;
    loadForm: (settings: FormSettings, sections: CreateSectionPayload[]) => void;
    reset: () => void;
}

type Store = State & Actions;

const defaultSettings: FormSettings = {
    title: '',
    description: '',
    visibility: 'public',
    requires_auth: false,
    one_response_per_user: true,
    closes_at: '',
    mode: 'form',
    shuffle_options: false,
    allow_edit_response: false,
    show_progress_bar: true,
    confirmation_message: 'Respuesta registrada',
    theme_color: '',
    theme_image_url: '',
    collect_email: false,
};

const initialState: State = {
    sections: [makeDefaultSection(0)],
    selectedSectionIndex: 0,
    selectedFieldIndex: 0,
    formSettings: defaultSettings,
    isDirty: false,
};

export const useFormBuilderStore = create<Store>((set) => ({
    ...initialState,

    addSection: () =>
        set((state) => {
            const sections = [
                ...state.sections,
                makeDefaultSection(state.sections.length),
            ];
            return { sections, isDirty: true, selectedSectionIndex: sections.length - 1, selectedFieldIndex: null };
        }),

    removeSection: (index) =>
        set((state) => {
            if (state.sections.length <= 1) return state;
            const sections = state.sections
                .filter((_, i) => i !== index)
                .map((s, i) => ({ ...s, order: i }));
            const selectedSectionIndex = Math.min(index, sections.length - 1);
            return { sections, isDirty: true, selectedSectionIndex, selectedFieldIndex: null };
        }),

    reorderSections: (from, to) =>
        set((state) => {
            const sections = [...state.sections];
            const [moved] = sections.splice(from, 1);
            sections.splice(to, 0, moved);
            return { sections: sections.map((s, i) => ({ ...s, order: i })), isDirty: true };
        }),

    updateSection: (index, partial) =>
        set((state) => {
            const sections = [...state.sections];
            sections[index] = { ...sections[index], ...partial };
            return { sections, isDirty: true };
        }),

    addField: (sectionIndex) =>
        set((state) => {
            if (state.sections.length === 0) return state;
            const sections = [...state.sections];
            const section = { ...sections[sectionIndex] };
            if (section.fields.length >= 50) return state;
            const newField = makeDefaultField(section.fields.length);
            section.fields = [...section.fields, newField];
            sections[sectionIndex] = section;
            return {
                sections,
                isDirty: true,
                selectedFieldIndex: section.fields.length - 1,
            };
        }),

    removeField: (sectionIndex, fieldIndex) =>
        set((state) => {
            const sections = [...state.sections];
            const section = { ...sections[sectionIndex] };
            if (section.fields.length <= 1) return state;
            section.fields = section.fields
                .filter((_, i) => i !== fieldIndex)
                .map((f, i) => ({ ...f, order: i }));
            sections[sectionIndex] = section;
            const selectedFieldIndex = Math.min(fieldIndex, section.fields.length - 1);
            return { sections, isDirty: true, selectedFieldIndex };
        }),

    reorderFields: (sectionIndex, from, to) =>
        set((state) => {
            const sections = [...state.sections];
            const section = { ...sections[sectionIndex] };
            const fields = [...section.fields];
            const [moved] = fields.splice(from, 1);
            fields.splice(to, 0, moved);
            section.fields = fields.map((f, i) => ({ ...f, order: i }));
            sections[sectionIndex] = section;
            return { sections, isDirty: true };
        }),

    updateField: (sectionIndex, fieldIndex, field) =>
        set((state) => {
            const sections = [...state.sections];
            const section = { ...sections[sectionIndex] };
            const fields = [...section.fields];
            fields[fieldIndex] = field;
            section.fields = fields;
            sections[sectionIndex] = section;
            return { sections, isDirty: true };
        }),

    updateSettings: (partial) =>
        set((state) => ({
            formSettings: { ...state.formSettings, ...partial },
            isDirty: true,
        })),

    setSelectedSection: (index) => set({ selectedSectionIndex: index, selectedFieldIndex: null }),

    setSelectedField: (index) => set({ selectedFieldIndex: index }),

    loadForm: (settings, sections) =>
        set({
            formSettings: settings,
            sections,
            selectedSectionIndex: 0,
            selectedFieldIndex: sections[0]?.fields.length > 0 ? 0 : null,
            isDirty: false,
        }),

    reset: () => set({ ...initialState }),
}));
